import { useMemo } from "react";
import { MyLeaveLeaveCardProps } from "../leave-card/leave-card";
import { LeaveTypesInJDolber } from "./leave-cards";
import { useMyLeaveSummaryChartData as useMyLeaveSummaryChartDataDI } from "my-leave/hooks/use-summary-chart-data";

type LeaveTypes = MyLeaveLeaveCardProps["type"];

type UseMyLeaveLeaveCardsProps = {
  contractCode: string;
  selectedYear: number;
  showFilter?: LeaveTypesInJDolber[];
  useMyLeaveSummaryChartData?: typeof useMyLeaveSummaryChartDataDI;
};

const LEAVE_TYPES_WITH_CODE: Record<LeaveTypes, LeaveTypesInJDolber> = {
  base: "S",
  study: "T",
  covid: "Q",
  eszjtv: "D",
  emergency: "V",
} as const;

type CleanDataType = {
  all: Record<LeaveTypes, number>;
  available: Record<LeaveTypes, number>;
  requested: Record<LeaveTypes, number>;
  fulfilled: Record<LeaveTypes, number>;
};

export const useMyLeaveLeaveCards = ({
  contractCode,
  selectedYear,
  showFilter,
  useMyLeaveSummaryChartData = useMyLeaveSummaryChartDataDI,
}: UseMyLeaveLeaveCardsProps) => {
  const { data } = useMyLeaveSummaryChartData({
    year: String(selectedYear),
    contract: contractCode,
  });

  const cleanData: CleanDataType = useMemo(
    () => ({
      all: {
        // The order of this determines the rendered order.
        base: data.allLeaveDays,
        study: data.allStudyLeaveDays,
        covid: data.allCovidPlus10LeaveDays,
        eszjtv: data.allESZJTV19LeaveDays,
        emergency: data.allEmergencyLeaveDays,
      },
      available: {
        base: data.availableLeaveDays,
        study: data.availableStudyLeaveDays,
        covid: data.availableCovidPlus10LeaveDays,
        eszjtv: data.availableESZJTV19LeaveDays,
        emergency: data.availableEmergencyLeaveDays,
      },
      requested: {
        base: data.requestedLeaveDays,
        study: data.requestedStudyLeaveDays,
        covid: data.requestedCovidPlus10LeaveDays,
        eszjtv: data.requestedESZJTV19LeaveDays,
        emergency: data.requestedEmergencyLeaveDays,
      },
      fulfilled: {
        base: data.fulfilledLeaveDays,
        study: data.fulfilledStudyLeaveDays,
        covid: data.fulfilledCovidPlus10Days,
        eszjtv: data.fulfilledESZJTV19Days,
        emergency: data.fulfilledEmergencyDays,
      },
    }),
    [data],
  );

  const neededCards = () => {
    const keys = Object.keys(cleanData.all) as LeaveTypes[];
    const neededCards = keys.filter((key) => {
      if (showFilter && !showFilter.includes(LEAVE_TYPES_WITH_CODE[key]))
        return false;

      return cleanData.all[key as keyof typeof cleanData.all] > 0;
    });
    return neededCards;
  };

  return {
    neededCards: neededCards(),
    data: cleanData,
  };
};
